import { Injectable } from '@angular/core';

const KB_ENVS = {
  dev:  { url: 'https://dev-va-apis.mobydyg.com', key: '6YY73H8017obPAk4K7m4ZD1935g5x4' },
  prod: { url: 'https://va-apis.mobydyg.com',     key: 'c95BhsUH77Qv0DbcnL5WyXu3htSpLzKhoL3bt6QjX6R9j1E2Q4J9B6tpY4xc' }
};

export interface KnowledgeBaseEntry {
  id: number;
  shopId: number;
  category: string;
  subject: string;
  context: string;
}

export type KnowledgeBasePayload = Omit<KnowledgeBaseEntry, 'id'>;

function normalize(raw: any): KnowledgeBaseEntry {
  return {
    id:       raw.id       ?? raw.Id       ?? raw.knowledgeBaseId ?? raw.KnowledgeBaseId,
    shopId:   raw.shopId   ?? raw.ShopId,
    category: raw.category ?? raw.Category ?? '',
    subject:  raw.subject  ?? raw.Subject  ?? '',
    context:  raw.context  ?? raw.Context  ?? ''
  };
}

@Injectable({
  providedIn: 'root'
})
export class KnowledgebaseService {
  private activeEnv: 'dev' | 'prod' = 'dev';

  setEnv(mode: 'dev' | 'prod') {
    this.activeEnv = mode;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const env = KB_ENVS[this.activeEnv];
    const url = `${env.url}${path}`;
    const config: RequestInit = {
      ...options,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-ApiKey': env.key
      }
    };
    const response = await fetch(url, config);
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`[KnowledgebaseService] ${response.status} ${response.statusText}: ${text}`);
    }
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }
    return response.json() as Promise<T>;
  }

  getByShopId(shopId: number): Promise<KnowledgeBaseEntry[]> {
    return this.request<any[]>(`/api/knowledgebase/shop/${shopId}`)
      .then(list => (Array.isArray(list) ? list : (list as any)?.data ?? []).map(normalize));
  }

  getById(id: number): Promise<KnowledgeBaseEntry> {
    return this.request<any>(`/api/knowledgebase/${id}`).then(normalize);
  }

  create(payload: KnowledgeBasePayload): Promise<KnowledgeBaseEntry> {
    return this.request<KnowledgeBaseEntry>('/api/knowledgebase', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  update(id: number, payload: KnowledgeBasePayload): Promise<KnowledgeBaseEntry> {
    return this.request<KnowledgeBaseEntry>(`/api/knowledgebase/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

  delete(id: number): Promise<void> {
    return this.request<void>(`/api/knowledgebase/${id}`, { method: 'DELETE' });
  }
}
